/**
 * hs-faq — category tabs over a single-open accordion.
 *
 * Progressive enhancement, in that order:
 *
 *   No JavaScript   every question is a <details> and every panel is visible.
 *                   The page is a complete, readable, expandable FAQ; the tabs
 *                   just don't filter.
 *   With JavaScript one panel at a time, one question open at a time, and the
 *                   tabs get proper roving-tabindex keyboard behaviour.
 *
 * Switching tabs resets to the first question of the new panel, as designed —
 * arriving at a category with everything collapsed reads as an empty page.
 */
class HSFaq extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-hs-tab]')];
    this.panels = [...this.querySelectorAll('[data-hs-panel]')];
    if (!this.panels.length) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onToggle = this.onToggle.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // <details> toggle does not bubble, so listen in the capture phase.
    this.addEventListener('toggle', this.onToggle, true);

    this.classList.add('is-enhanced');
    if (this.tabs.length) this.select(this.tabs[0].dataset.hsTab, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('toggle', this.onToggle, true);
  }

  onClick(event) {
    const tab = event.target.closest('[data-hs-tab]');
    if (tab) this.select(tab.dataset.hsTab, { focus: false });
  }

  onKeydown(event) {
    const tab = event.target.closest('[data-hs-tab]');
    if (!tab) return;

    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
    const move = keys[event.key];
    if (move === undefined) return;
    event.preventDefault();

    const i = this.tabs.indexOf(tab);
    let next;
    if (move === 'first') next = 0;
    else if (move === 'last') next = this.tabs.length - 1;
    else next = (i + move + this.tabs.length) % this.tabs.length;

    this.select(this.tabs[next].dataset.hsTab, { focus: true });
  }

  /** One question open at a time within a panel. */
  onToggle(event) {
    const item = event.target;
    if (!item.matches('details') || !item.open) return;
    const panel = item.closest('[data-hs-panel]');
    if (!panel) return;
    panel.querySelectorAll('details[open]').forEach((other) => {
      if (other !== item) other.open = false;
    });
  }

  select(key, { focus }) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.hsTab === key;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.hsPanel === key;
      panel.hidden = !active;
      if (!active) return;
      // Reset to the first question: a category that opens fully collapsed
      // reads as an empty page.
      const items = [...panel.querySelectorAll('details')];
      items.forEach((item, i) => {
        item.open = i === 0;
      });
    });
  }
}

if (!customElements.get('hs-faq')) {
  customElements.define('hs-faq', HSFaq);
}
